import { useEffect, useCallback, useState, useRef } from "react";
import { supabase } from "./supabase";
import { C, TM, TRADES, ALL_TRADES } from "./lib/constants";
import { ini, normalizeMode, clearFicioStorage, validateImageFile, adaptWorker, adaptSupervisor, mergeWorkersStable, isPremiumWorker } from "./lib/helpers";
import { FICIO_DIRECTORY_TTL_MS, FICIO_DIRECTORY_CACHE, saveDirectoryCache, upsertDirectoryWorkerCache } from "./lib/cache";
import { uploadPublicFile } from "./lib/storage";
import { Stars } from "./components/ui/Stars";
import { Btn } from "./components/ui/Btn";
import { Av } from "./components/ui/Av";
import { Inp } from "./components/ui/Inp";
import { TopNav } from "./components/ui/TopNav";
import { BottomNav } from "./components/ui/BottomNav";
import { FicioToast } from "./components/ui/FicioToast";
import { Card } from "./components/ui/Card";
import { Chip } from "./components/ui/Chip";
import { Landing } from "./components/Landing";
import { ObraActiva } from "./components/ObraActiva";
import { getFlowInfo, EstadoChip, FlowTimeline, MediationBox, EmptyBox, MediacionModal } from "./components/WorkFlow";
import { SolicitudesTrabajador } from "./components/SolicitudesTrabajador";
import { FileUploadCard, PhotoGrid } from "./components/ui/FileUpload";
import { Field } from "./components/ui/Field";
import { RegWorker, EditarPerfilMaestro } from "./components/RegWorker";
import { RegClient } from "./components/RegClient";
import { AdminPanel, ClientesAdminLocal } from "./components/AdminPanel";
import { FicioAuthScreen, WorkerLoginScreen, ClienteLoginScreen } from "./components/AuthScreens";
import { CuentaScreen } from "./components/CuentaScreen";
import { LoginRequiredBox } from "./components/ui/LoginRequiredBox";
import { Directory } from "./components/Directory";
import { WorkerProfile, PremiumBadge, VerifiedBadge, Bar } from "./components/WorkerProfile";
import { SupervisionFlow } from "./components/SupervisionFlow";
import { RequestWorkModal } from "./components/RequestWorkModal";
import { RegSupervisor } from "./components/RegSupervisor";
import { BuscarAyudante } from "./components/BuscarAyudante";
import { TrabajosCliente } from "./components/TrabajosCliente";
import { PerfilCliente, InfoMini } from "./components/PerfilCliente";
import { PortalCliente } from "./components/PortalCliente";
import { CalificarTrabajo } from "./components/CalificarTrabajo";




// ─── APP ──────────────────────────────────────────────────────────────────────







export default function App() {
  // Detectar ?resena=solicitudId en la URL para abrir calificación directa
  const urlParams = new URLSearchParams(window.location.search);
  const resenaParam = urlParams.get("resena");

  const [screen,   setScreen]   = useState(resenaParam ? "calificar-trabajo" : "landing");
  const [selected, setSelected] = useState(null);
  const [resenaTarget, setResenaTarget] = useState(resenaParam || null);
  const [requestWorker, setRequestWorker] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [hasCliente, setHasCliente] = useState(false);
  const [hasTrabajador, setHasTrabajador] = useState(false);
  // FIX CACHÉ: no usar localStorage como estado inicial del modo activo.
  // El modo se establece en cargarPerfil() después de validar contra la DB.
  // Iniciar siempre en "cliente" hasta que la sesión y el perfil carguen.
  const [activeMode, setActiveModeState] = useState("cliente");
  const activeModeRef = useRef(activeMode);
  const signingOutRef = useRef(false);
  const userRef = useRef(null);
  const screenRef = useRef(screen);
  useEffect(() => { activeModeRef.current = activeMode; }, [activeMode]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { screenRef.current = screen; }, [screen]);

  // Reset profile immediately when the authenticated user changes — prevents stale
  // data from a previous session ever being displayed for a different user.
  useEffect(() => {
    setProfile(null);
  }, [user?.id]);

  const refreshRoles = useCallback(async () => {
    if (!user?.id) { setHasCliente(false); setHasTrabajador(false); return; }
    const [{ data: c }, { data: t }] = await Promise.all([
      supabase.from("clientes").select("user_id").eq("user_id", user.id).maybeSingle(),
      supabase.from("trabajadores").select("user_id").eq("user_id", user.id).neq("archivado", true).maybeSingle(),
    ]);
    setHasCliente(!!c);
    setHasTrabajador(!!t);
  }, [user?.id]);

  useEffect(() => { refreshRoles(); }, [refreshRoles]);

  const setActiveMode = useCallback((mode) => {
    const next = normalizeMode(mode);
    activeModeRef.current = next;
    setActiveModeState(prev => prev === next ? prev : next);
  }, []);

  useEffect(() => {
    if (screen === "reg-worker" && hasTrabajador) { setScreen("obra-activa"); return; }
    if (screen === "reg-client" && hasCliente)    { setScreen("obra-activa"); return; }
  }, [screen, hasTrabajador, hasCliente]);
  // FIX E1: Barra visible en TODAS las pantallas si user está logueado, excepto onboarding/login/workerProfile.
  // landing ya NO está en lista negra — usuario logueado ve barra también en pantalla de Inicio.
  const showNav = !["workerProfile","reg-worker","reg-client","reg-supervisor",
                    "worker-login","cliente-login","calificar-trabajo","editar-maestro"].includes(screen) && Boolean(user);

  // Paso 29: estado central del directorio.
  // La lista vive en App.js para que NO desaparezca al cambiar pestañas,
  // entrar a Mi Obra o alternar Cliente/Trabajador.
  const directoryRequestRef = useRef(null);
  const [directoryData, setDirectoryData] = useState(() => ({
    workers: FICIO_DIRECTORY_CACHE.workers || [],
    supervisors: FICIO_DIRECTORY_CACHE.supervisors || [],
    clients: FICIO_DIRECTORY_CACHE.clients || [],
  }));
  const [directoryLoading, setDirectoryLoading] = useState(() => !(FICIO_DIRECTORY_CACHE.workers || []).length);

  const refreshDirectory = useCallback(async ({ force = false } = {}) => {
    const cacheTieneTrabajadores = (FICIO_DIRECTORY_CACHE.workers || []).length > 0;
    const cacheVigente = Date.now() - (FICIO_DIRECTORY_CACHE.loadedAt || 0) < FICIO_DIRECTORY_TTL_MS;

    // Paso 33: no vaciar ni regresar el directorio a un cache viejo al cambiar pantallas.
    // Primero conserva lo que ya está en estado; solo usa cache cuando la app abre en frío.
    setDirectoryData(prev => ({
      workers: prev.workers?.length ? prev.workers : (FICIO_DIRECTORY_CACHE.workers || []),
      supervisors: prev.supervisors?.length ? prev.supervisors : (FICIO_DIRECTORY_CACHE.supervisors || []),
      clients: prev.clients?.length ? prev.clients : (FICIO_DIRECTORY_CACHE.clients || []),
    }));
    setDirectoryLoading(prev => cacheTieneTrabajadores ? false : prev);

if (!force && cacheTieneTrabajadores && cacheVigente) return;
    if (force) directoryRequestRef.current = false;
    if (directoryRequestRef.current) return;
    directoryRequestRef.current = true;

    try {
      // FIX AUDIT: clientes.select("*") eliminado — innecesario para el directorio,
      // expone datos de todos los clientes y viola principio de mínimo privilegio.
      // El directorio solo necesita trabajadores y supervisores.
      const { data, error } = await supabase
        .from("trabajadores")
        .select("*")
        .neq("archivado", true)
        .or("visible_directorio.eq.true,visible_directorio.is.null")
        .order("plan", { ascending: false })
        .order("calificacion", { ascending: false });

      if (error) {
        console.error("FICIO directorio error:", error.message, error.code);
        return;
      }
      console.log("FICIO directorio OK - total:", data?.length);

      const supervisorsRes = await supabase.from("supervisores").select("*");
      if (supervisorsRes.error) console.warn("Error supervisores:", supervisorsRes.error.message);

      const trabajadores = data || [];
      // Server already orders by plan DESC, calificacion DESC — preserve that order, just deduplicate
      const vistos = new Set();
      const fuenteFinal = trabajadores.filter(w => {
        const key = w.id;
        if (!key || vistos.has(String(key))) return false;
        vistos.add(String(key));
        return true;
      });
      const nextWorkers = fuenteFinal.map(adaptWorker);
      const sortByDate = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
      const nextSupervisors = (supervisorsRes.error ? [] : supervisorsRes.data || []).sort(sortByDate).map(adaptSupervisor);

      setDirectoryData(prev => {
        const finalData = {
          workers: nextWorkers.length ? nextWorkers : (prev.workers?.length ? prev.workers : FICIO_DIRECTORY_CACHE.workers || []),
          supervisors: nextSupervisors.length ? nextSupervisors : (prev.supervisors?.length ? prev.supervisors : FICIO_DIRECTORY_CACHE.supervisors || []),
          clients: prev.clients?.length ? prev.clients : [],
        };
        saveDirectoryCache(finalData);
        return finalData;
      });
    } catch (err) {
      console.warn("No se pudo refrescar directorio central:", err);
      setDirectoryData(prev => ({
        workers: prev.workers?.length ? prev.workers : (FICIO_DIRECTORY_CACHE.workers || []),
        supervisors: prev.supervisors?.length ? prev.supervisors : (FICIO_DIRECTORY_CACHE.supervisors || []),
        clients: prev.clients?.length ? prev.clients : (FICIO_DIRECTORY_CACHE.clients || []),
      }));
    } finally {
      setDirectoryLoading(false);
      directoryRequestRef.current = false;
    }
  }, []);

  const cargarPerfil = useCallback(async (sessionUser=null) => {
    const actualUser = sessionUser || userRef.current;
    if (!actualUser?.id) return;

    // La DB tiene prioridad absoluta sobre localStorage para evitar mostrar
    // datos de usuarios borrados o roles inexistentes.
    const [perfilRes, trabajadorRes, clienteRes] = await Promise.all([
      supabase.from("perfiles").select("*").eq("user_id", actualUser.id).neq("archivado", true).single(),
      supabase.from("trabajadores").select("id,plan,membresia_activa,nombre,telefono,foto_url,avatar,ine_url").eq("user_id", actualUser.id).neq("archivado", true).maybeSingle(),
      supabase.from("clientes").select("nombre,telefono,correo,foto_url,avatar,ine_url").eq("user_id", actualUser.id).maybeSingle(),
    ]);

    let perfilData = perfilRes.data || null;
    const wData = trabajadorRes.data || null;
    const cData = clienteRes.data || null;

    // Registration gate: if Auth user has no public rows at all, it is a ghost account.
    if (!perfilData && !wData && !cData) {
      console.warn("FICIO: Auth user has no public profile rows — blocking access.");
      signingOutRef.current = true;
      await supabase.auth.signOut();
      clearFicioStorage();
      signingOutRef.current = false;
      setUser(null);
      setProfile(null);
      // Stay on login screens so entrar() can show its error message.
      const loginScreens = ["cliente-login","worker-login","landing","reg-client","reg-worker","reg-supervisor"];
      if (!loginScreens.includes(screenRef.current)) setScreen("landing");
      return true; // blocked
    }

    // Fill missing foto/ine from other tables (DB only — never from user_metadata).
    // nombre and telefono are NOT merged here: perfiles is the sole authoritative source.
    // If perfiles.nombre is blank, Mi cuenta shows blank — the user must fill it via edit screen.
    if (perfilData) {
      const first = (...vals) => vals.find(v => v && String(v).trim()) || "";
      if (!perfilData.foto_url && !perfilData.avatar_url) {
        const merged = first(cData?.foto_url, cData?.avatar, wData?.foto_url, wData?.avatar);
        if (merged) perfilData = { ...perfilData, foto_url: merged };
      }
      if (!perfilData.ine_url) {
        const merged = first(cData?.ine_url, wData?.ine_url);
        if (merged) perfilData = { ...perfilData, ine_url: merged };
      }
    }

    setProfile(perfilData);

    // Mode comes exclusively from perfiles table in DB — no localStorage fallback.
    const rawModeDB = perfilData?.modo_activo || perfilData?.active_mode
                   || perfilData?.role        || perfilData?.rol;
    let candidato = normalizeMode(rawModeDB || "cliente");

    // If DB says "trabajador" but there is no trabajadores row, fall back to "cliente".
    if (candidato === "trabajador" && !trabajadorRes.data) candidato = "cliente";

    setActiveMode(candidato, actualUser?.id || null);
    await refreshRoles();
    return false; // not blocked
  }, [setActiveMode]);

  // Initial session load — runs once on mount. Supabase Auth is the sole source of truth.
  useEffect(() => {
    const cargarSesion = async () => {
      const { data } = await supabase.auth.getSession();
      const sessionUser = data?.session?.user || null;

      if (sessionUser) {
        setUser(sessionUser);
        userRef.current = sessionUser;
        await cargarPerfil(sessionUser);
      } else {
        setUser(null);
        setProfile(null);
        setScreen("landing");
      }
    };

    cargarSesion();
  }, []); // eslint-disable-line

  // Listen for subsequent auth changes (login / logout) after the initial load.
  useEffect(() => {
    let mounted = true;

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // INITIAL_SESSION is handled by cargarSesion above — skip to avoid double-load.
      if (event === "INITIAL_SESSION") return;

      const sessionUser = session?.user || null;
      if (signingOutRef.current && sessionUser) return;

      setUser(sessionUser);
      userRef.current = sessionUser;

      if (sessionUser) {
        // FIX F1-2: no forzar "cliente" antes de cargarPerfil — el modo correcto
        // lo determina la DB (perfiles.modo_activo). Forzarlo aquí pisaba el rol del trabajador.
        const blocked = await cargarPerfil(sessionUser);
        // Do not redirect if blocked (ghost user) or mid-registration flow.
        if (!blocked && event === "SIGNED_IN" && !["reg-worker","reg-client","reg-supervisor"].includes(screenRef.current)) {
          // FIX F1-3: redirigir según el rol real, no siempre a obra-activa.
          const mode = activeModeRef.current;
          setScreen(mode === "trabajador" ? "solicitudes-worker" : "obra-activa");
        }
      } else {
        setUser(null);
        setProfile(null);
        setSelected(null);
        setRequestWorker(null);
        setActiveMode("cliente");
        // Do not navigate away from login/registration screens — entrar() may be
        // displaying an error message after a failed registration check + signOut.
        const loginScreens = ["cliente-login","worker-login","landing","reg-client","reg-worker","reg-supervisor"];
        if (!loginScreens.includes(screenRef.current)) setScreen("landing");
      }
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, [cargarPerfil, setActiveMode]);

  // FIX AUDIT: separar la carga del directorio por trigger real
  // Carga inicial garantizada al montar la app
  useEffect(() => {
    refreshDirectory({ force: true });
  }, []); // eslint-disable-line

  // Antes: [user, screen] causaba una query a Supabase en CADA cambio de pantalla
  // Ahora: solo cuando cambia user (login/logout) o al montar
  const userIdRef = useRef(null);
  useEffect(() => {
    const userId = user?.id || null;
    if (userId !== userIdRef.current) {
      userIdRef.current = userId;
      refreshDirectory({ force: false });
    }
  }, [user?.id, refreshDirectory]);

  // Precarga adicional solo en pantallas clave, pero protegida contra re-ejecución en cada tap
  const screenLoadedRef = useRef({});
  useEffect(() => {
    const key = screen;
    if (["directory","obra-activa","landing"].includes(key) && !screenLoadedRef.current[key]) {
      screenLoadedRef.current[key] = true;
      refreshDirectory({ force: false });
    }
  }, [screen, refreshDirectory]);

  useEffect(() => {
    const refrescarDirectorio = () => refreshDirectory({ force: true });
    const channel = supabase
      .channel("ficio-directorio-global-v29")
      .on("postgres_changes", { event: "*", schema: "public", table: "trabajadores" }, refrescarDirectorio)
      .on("postgres_changes", { event: "*", schema: "public", table: "supervisores" }, refrescarDirectorio)
      // FIX AUDIT: clientes ya no se carga en el directorio — quitado de realtime sub
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [refreshDirectory]);

  const cerrarSesion = async () => {
    try {
      setScreen("landing");
      setRequestWorker(null);
      setSelected(null);
      setUser(null);
      setProfile(null);
      setActiveMode("cliente");
      await supabase.auth.signOut();
      localStorage.clear();
      sessionStorage.clear();
    } catch(e) {
      console.warn("Error cerrando sesión:", e.message);
      window.location.href = "/";
    }
  };

  return (
    <div style={{background:C.bg,minHeight:"100vh"}}>
      <style>{`
        
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:4px;height:4px;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:#DDD8D0;border-radius:4px;}
        input::placeholder,textarea::placeholder{color:#C0BBB5;}
        select option{background:#fff;color:#1A1612;}
        button{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
      `}</style>

      <FicioToast/>
      {screen==="landing"           && <Landing        setScreen={setScreen} user={user} profile={profile} activeMode={activeMode} setActiveMode={setActiveMode} directoryData={directoryData}/>}
      {screen==="directory"         && <Directory      setScreen={setScreen} setSelected={setSelected} user={user} directoryData={directoryData} directoryLoading={directoryLoading} refreshDirectory={refreshDirectory} hasTrabajador={hasTrabajador}/>}
      {screen==="workerProfile"     && <WorkerProfile  setScreen={setScreen} selected={selected} setRequestWorker={setRequestWorker} user={user}/>}
      {screen==="supervision-flow"  && (
        <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
          <TopNav setScreen={setScreen} back="directory" backLabel="Buscar" title="Contratar supervisión"/>
          <div style={{maxWidth:640,margin:"0 auto",padding:"20px 20px 0"}}>
            <SupervisionFlow user={user} profile={profile} setScreen={setScreen}/>
          </div>
        </div>
      )}
      {screen==="obra-activa"       && <ObraActiva     setScreen={setScreen} user={user} profile={profile} activeMode={activeMode} setActiveMode={setActiveMode}/>}
      {screen==="solicitudes-worker"&& <SolicitudesTrabajador setScreen={setScreen} user={user} profile={profile}/>}
      {screen==="perfil-cliente"   && <PerfilCliente setScreen={setScreen} user={user} profile={profile}/>}
      {screen==="admin"             && <AdminPanel setScreen={setScreen}/>}
      {screen==="clientes-list"    && <ClientesAdminLocal setScreen={setScreen}/>}
      {screen==="trabajos-cliente"  && <TrabajosCliente setScreen={setScreen} user={user} profile={profile}/>}
      {screen==="buscar-ayudante"   && <BuscarAyudante setScreen={setScreen} user={user}/>}
      {screen==="cliente-login"    && <ClienteLoginScreen setScreen={setScreen} onAuthReady={()=>cargarPerfil?.()}/>}
      {screen==="worker-login"     && <WorkerLoginScreen setScreen={setScreen} onAuthReady={()=>cargarPerfil?.()}/>}
      {screen==="cuenta"          && <CuentaScreen setScreen={setScreen} user={user} profile={profile} activeMode={activeMode} setActiveMode={setActiveMode} onLogout={cerrarSesion}/>}
      {screen==="portal-cliente"  && <PortalCliente setScreen={setScreen} user={user} profile={profile} onLogout={cerrarSesion}/>}
      {screen==="reg-worker"        && <RegWorker      setScreen={setScreen} user={user} refreshDirectory={refreshDirectory} onRegistered={refreshRoles}/>}
      {screen==="editar-maestro"    && <EditarPerfilMaestro setScreen={setScreen} user={user} refreshDirectory={refreshDirectory}/>}
      {screen==="reg-client"        && <RegClient      setScreen={setScreen} user={user} onRegistered={refreshRoles}/>}
      {screen==="reg-supervisor"    && <RegSupervisor  setScreen={setScreen} user={user}/>}

      {screen==="calificar-trabajo" && resenaTarget && (
        <CalificarTrabajo
          solicitudId={resenaTarget}
          user={user}
          setScreen={setScreen}
          onDone={()=>{
            setResenaTarget(null);
            setScreen("landing");
            window.history.replaceState({}, "", window.location.pathname);
          }}
        />
      )}
      {requestWorker && <RequestWorkModal worker={requestWorker} user={user} profile={profile} setScreen={setScreen} onClose={()=>setRequestWorker(null)}/>}

      {showNav && <BottomNav screen={screen} setScreen={setScreen} user={user} onLogout={cerrarSesion}/>}
    </div>
  );
}
